import { FileItem, FileUploader, FileUploaderOptions, ParsedResponseHeaders } from 'ng2-file-upload';
import { Subscription } from 'rxjs';
import { AuthFacade } from '@iris/modules/auth/utils/auth.facade';
import { tap } from 'rxjs/operators';
import { inject, Injectable } from '@angular/core';
import { AbstractDatetime } from '@iris/common/modules/date';
import { DmsFileNamePart, getFileNamePart } from '@iris/common/utils/file.utils';
import { IrisInfrastructureService } from '@iris/common/services/infrastructure.service';

export interface AuthFileUploaderOptions extends FileUploaderOptions {
  uniqueNameOnTablet?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AuthFileUploaderFactory {
  private readonly authFacade = inject(AuthFacade);
  private readonly infrastructureService = inject(IrisInfrastructureService);

  instance(options: AuthFileUploaderOptions): AuthFileUploader {
    return new AuthFileUploader(this.authFacade, this.infrastructureService, options);
  }
}

export class AuthFileUploader extends FileUploader {
  protected readonly fileItemSubscriptionMap = new Map<FileItem, Subscription>();

  protected readonly uniqueNameOnTablet: boolean;

  constructor(
    protected readonly authFacade: AuthFacade,
    protected readonly infrastructureService: IrisInfrastructureService,
    options: AuthFileUploaderOptions,
  ) {
    super(options);
    this.uniqueNameOnTablet = !!options?.uniqueNameOnTablet;
  }

  addToQueue(files: File[], _options?: FileUploaderOptions, filters?: [] | string): void {
    let filesArray = Array.from(files);
    if (this.uniqueNameOnTablet && (this.infrastructureService?.isTablet || this.infrastructureService?.isMobile)) {
      filesArray = filesArray.map(file => {
        const lastModified = AbstractDatetime.parse(file.lastModified);
        const blob = file.slice(0, file.size, file.type);
        const name = getFileNamePart(file.name, DmsFileNamePart.BaseName);
        const extension = getFileNamePart(file.name, DmsFileNamePart.Extension);
        const updatedName = `${name}-${lastModified.toIsoString({ includeOffset: false })}.${extension}`;
        return new File([blob], updatedName, { type: file.type });
      });
    }
    super.addToQueue(filesArray, _options, filters);
  }

  uploadItem(value: FileItem): void {
    const index = this.getIndexOfItem(value);
    const item = this.queue[index];
    if (!item) { return; }

    this.fileItemSubscriptionMap.get(item)?.unsubscribe();
    this.fileItemSubscriptionMap.set(item, this.authFacade.actualToken(true).pipe(
      tap((authToken) => {
        if (authToken) {
          this.authToken = `Bearer ${authToken}`;
        }
        super.uploadItem(value);
      }),
    ).subscribe());
  }

  cancelItem(value: FileItem): void {
    const index = this.getIndexOfItem(value);
    const item = this.queue[index];
    if (!item) { return; }

    this.fileItemSubscriptionMap.get(item)?.unsubscribe();
    this.fileItemSubscriptionMap.delete(item);

    super.cancelItem(value);
  }

  _onCompleteItem(item: FileItem, response: string, status: number, headers: ParsedResponseHeaders): void {
    this.fileItemSubscriptionMap.get(item)?.unsubscribe();
    this.fileItemSubscriptionMap.delete(item);
    super._onCompleteItem(item, response, status, headers);
  }
}
